import { LOCALES, LOCALE_DIRECTION, type Locale } from '@tablet-service/contracts';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import arAuth from '../locales/ar/auth.json';
import arCommon from '../locales/ar/common.json';
import arErrors from '../locales/ar/errors.json';
import arLayout from '../locales/ar/layout.json';
import arSettings from '../locales/ar/settings.json';
import arDevice from '../locales/ar/device.json';
import arPhone from '../locales/ar/phone.json';
import arOrganisation from '../locales/ar/organisation.json';
import arSystems from '../locales/ar/systems.json';
import arTablets from '../locales/ar/tablets.json';
import arValidation from '../locales/ar/validation.json';
import enAuth from '../locales/en/auth.json';
import enCommon from '../locales/en/common.json';
import enErrors from '../locales/en/errors.json';
import enLayout from '../locales/en/layout.json';
import enSettings from '../locales/en/settings.json';
import enDevice from '../locales/en/device.json';
import enPhone from '../locales/en/phone.json';
import enOrganisation from '../locales/en/organisation.json';
import enSystems from '../locales/en/systems.json';
import enTablets from '../locales/en/tablets.json';
import enValidation from '../locales/en/validation.json';

export const DEFAULT_LOCALE: Locale = 'ar';
export const STORAGE_KEY = 'app_language';
export const resources = {
  ar: {
    common: arCommon,
    auth: arAuth,
    errors: arErrors,
    layout: arLayout,
    validation: arValidation,
    settings: arSettings,
    systems: arSystems,
    tablets: arTablets,
    organisation: arOrganisation,
    device: arDevice,
    phone: arPhone,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    errors: enErrors,
    layout: enLayout,
    validation: enValidation,
    settings: enSettings,
    systems: enSystems,
    tablets: enTablets,
    organisation: enOrganisation,
    device: enDevice,
    phone: enPhone,
  },
} as const;
export const NAMESPACES = [
  'common',
  'auth',
  'errors',
  'layout',
  'validation',
  'settings',
  'systems',
  'tablets',
  'organisation',
  'device',
  'phone',
] as const;

// Arabic is the initial and fallback language (AGENTS.md); only an explicit
// saved choice overrides it, browser language is not treated as a choice.
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    ns: [...NAMESPACES],
    defaultNS: 'common',
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

/** Keep direction, typography and document identity aligned with the saved locale. */
export function applyLocale(locale: Locale): void {
  const root = document.documentElement;
  const fontFamily = locale === 'ar' ? 'Cairo, system-ui, sans-serif' : 'Inter, sans-serif';
  root.lang = locale;
  root.dir = LOCALE_DIRECTION[locale];
  root.setAttribute('data-locale', locale);
  root.style.fontFamily = fontFamily;
  if (document.body) document.body.style.fontFamily = fontFamily;
  document.title = i18n.t('appName', { ns: 'common' });
}

export async function changeLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  applyLocale(locale);
}

export function currentLocale(): Locale {
  const language = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  return (LOCALES as readonly string[]).includes(language) ? (language as Locale) : DEFAULT_LOCALE;
}
applyLocale(currentLocale());
export default i18n;
